import { useCanAccess, useRecordContext, useTranslate } from "ra-core";
import { DataTable } from "@/components/admin/data-table";
import { InfiniteList } from "@/components/admin/infinite-list";
import { DateField } from "@/components/admin";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useScrollAreaRestoration } from "../misc/useScrollAreaRestoration";
import { ReviewStatusField } from "./ReviewStatusField";

import { FullHeightListLayout } from "../misc/FullHeightListLayout";
import { InfinitePagination } from "../misc/InfinitePagination";
import type { ZoomMeetingSummaryView } from "../types";
import { namesTitle } from "./meetingDisplayTitle";
import { MeetingAssetIcons } from "./MeetingAssetIcons";
import { MeetingNameCell } from "./MeetingNameCell";
import { MeetingPermission } from "./MeetingPermission";
import { refetchWhileAwaiting, rowsFromQueryData } from "./meetingAssets";
import { ActiveFilterBar } from "../misc/ActiveFilterBar";
import { useZoomMeetingFilterBar } from "./zoomMeetingFilterBar";
import { ZoomMeetingListFilter } from "./ZoomMeetingListFilter";
import { TranscriptExcerpt } from "./TranscriptExcerpt";
import {
  TranscriptMatchesProvider,
  useTranscriptSearchCapped,
} from "./useTranscriptMatches";

// The raw Zoom topic, shown honestly. Generic topics ("Zoom Meeting", "X's
// Personal Meeting Room") are muted — they mean the meeting was never actually
// scheduled, so there is no name to show and the Attendees column is what
// identifies the row. Muting them also makes the un-named calls obvious at a
// glance, which is the nudge toward scheduling them properly.
const MeetingTopicField = () => {
  const r = useRecordContext<ZoomMeetingSummaryView>();
  if (!r) return null;
  return (
    <>
      <MeetingNameCell record={r} />
      {/* Only when the search matched inside this meeting's transcript. */}
      <TranscriptExcerpt meetingId={r.id} linkable />
    </>
  );
};

const PermissionField = () => {
  const r = useRecordContext<ZoomMeetingSummaryView>();
  if (!r) return null;
  return <MeetingPermission meeting={r} />;
};

// A common word can match most transcripts; say so when only the most recent
// 200 were searched.
const TranscriptCapNotice = () => {
  const translate = useTranslate();
  if (!useTranscriptSearchCapped()) return null;
  return (
    <p className="text-xs text-muted-foreground px-2 pb-2">
      {translate("resources.zoom_meetings.transcript_search.capped")}
    </p>
  );
};

// Who was on the call. The view returns names in join order, so the host leads
// (they start the meeting) — that is why there is no separate Host column.
const AttendeesField = () => {
  const r = useRecordContext<ZoomMeetingSummaryView>();
  if (!r) return null;
  const names = r.participant_names ?? [];
  const title = namesTitle(names);
  if (!title) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="block truncate" title={names.join(", ")}>
      {title}
    </span>
  );
};

const AssetIconsField = () => {
  const r = useRecordContext<ZoomMeetingSummaryView>();
  if (!r) return null;
  return <MeetingAssetIcons record={r} linkable />;
};

// Who ran the call. `host_name` comes from matching the host's email against
// their own attendee roster, so it reads "Elijah" rather than the full address;
// meetings whose roster we never fetched fall back to the email's local-part.
const HostField = () => {
  const r = useRecordContext<ZoomMeetingSummaryView>();
  if (!r) return null;
  const email = r.meeting_host_email ?? "";
  const name = (r.host_name ?? "").trim() || email.split("@")[0];
  if (!name) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="block truncate" title={email || undefined}>
      {name.split(/\s+/)[0]}
    </span>
  );
};

export function ZoomMeetingList() {
  return (
    <InfiniteList
      actions={false}
      filterDefaultValues={{ "has_content@eq": true }}
      sort={{ field: "meeting_start_time", order: "DESC" }}
      perPage={25}
      // A just-ended meeting is listed before Zoom has delivered anything; keep
      // checking until its files arrive or its waiting window closes.
      queryOptions={{
        refetchInterval: (query) =>
          refetchWhileAwaiting(
            rowsFromQueryData<ZoomMeetingSummaryView>(query.state.data),
          ),
      }}
    >
      <ZoomMeetingListLayout />
    </InfiniteList>
  );
}

const ZoomMeetingListLayout = () => {
  const viewportRef = useScrollAreaRestoration();
  const filterBar = useZoomMeetingFilterBar();
  // Admins only: everyone else has no say in who sees a meeting. LCCRM-93.
  const { canAccess: canManageAccess } = useCanAccess({
    resource: "zoom_meeting_access",
    action: "edit",
  });
  return (
    <FullHeightListLayout
      sidebar={<ZoomMeetingListFilter />}
      header={<ActiveFilterBar {...filterBar} className="border-b px-4 py-2" />}
    >
      <TranscriptMatchesProvider>
        <ScrollArea viewportRef={viewportRef} className="flex-1 min-h-0">
          <TranscriptCapNotice />
          <DataTable>
            {/* Year is omitted: the list sorts newest-first and the data spans a
            single year. Revisit if meetings ever span years. */}
            <DataTable.Col
              source="meeting_start_time"
              label="resources.zoom_meetings.fields.meeting_start_time"
              className="w-[12%]"
            >
              <DateField
                source="meeting_start_time"
                options={{
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                }}
              />
            </DataTable.Col>
            <DataTable.Col source="meeting_topic" className="w-[24%]">
              <MeetingTopicField />
            </DataTable.Col>
            <DataTable.Col
              label="resources.zoom_meetings.fields.attendees"
              className="w-[26%]"
            >
              <AttendeesField />
            </DataTable.Col>
            <DataTable.Col source="meeting_host_email" className="w-[10%]">
              <HostField />
            </DataTable.Col>
            {/* Status + outstanding drafts, merged. Deliberately has no `source`:
            that keeps it unsortable, and `tasks_remaining` is a per-row subquery
            that must never drive an ORDER BY or filter (see 03_views.sql). */}
            <DataTable.Col
              label="resources.zoom_meetings.fields.status"
              className="w-[14%]"
            >
              <ReviewStatusField />
            </DataTable.Col>
            <DataTable.Col
              label="resources.zoom_meetings.fields.content"
              className="w-[14%]"
            >
              <AssetIconsField />
            </DataTable.Col>
            {canManageAccess && (
              <DataTable.Col
                label="resources.zoom_meetings.fields.permission"
                className="w-[10%]"
              >
                <PermissionField />
              </DataTable.Col>
            )}
          </DataTable>
          <InfinitePagination />
        </ScrollArea>
      </TranscriptMatchesProvider>
    </FullHeightListLayout>
  );
};
